import React, { useEffect, useState } from "react";

import { Box, Grid } from "@mui/material";
import Typography from "@mui/material/Typography";

import PageContainer from "../components/shared/PageContainer";
import QueueCard, { QueueInfo } from "../components/stats/QueueCard";
import StatsColumn from "../components/stats/StatsColumn";
import { useConfig } from "../contexts/ConfigContext";
import * as Objects from "../services/api";
import { BackendConfiguration, JobStats, TaskStats } from "../services/api/Stats";
import { promiseWithResolvers } from "../services/utils/promise";

type DisplayItem = {
  label: string;
  value: string;
};

type DisplayConfig<T> = {
  [K in keyof T]: string | { label: string; formatter: (value: any) => string; hide?: boolean };
};

const backendConfigurationDisplayConfig: DisplayConfig<BackendConfiguration> = {
  number_of_workers: "Number of Workers",
  delay_between_stitcher_registration_polling: "Delay Between Stitcher Registration Polling",
  s3_bucket_region: "S3 Bucket Region",
  s3_move_concurrency: "S3 Move Concurrency",
  s3_copy_concurrency: "S3 Copy Concurrency",
  sqs_visibility_timeout: "SQS Visibility Timeout",
  sqs_max_receive_count: "SQS Max Receive Count",
  transcoder_region: "Transcoder Region",
  primary_stitcher_regions: { label: "? Stitcher Regions", formatter: (v: string[]) => v.join(", ") },
  primary_media_file_host: "? Media File Host",
  secondary_stitcher_regions: { label: "? Stitcher Regions", formatter: (v: string[]) => v.join(", ") },
  secondary_media_file_host: { label: "? Media File Host", formatter: (v: string) => v },
  output_bucket: "Output Bucket",
  output_prefix: "Output Prefix",
  upload_bucket: "Upload Bucket",
  ad_server_api: "Ad Server API",
  job_creation_limiter_period_minutes: "Job Creation Limiter Period (minutes)",
  job_creation_limiter_max_submission: "Job Creation Limiter Max Submission",
  job_creation_limiter_url_comparison_parts: "Job Creation Limiter URL Comparison Parts",
  set_id_field_selector_expr: "Set ID Field Selector Expression",
  set_id_field_value_regex: "Set ID Field Value Regular Expression",
};

const jobStatsDisplayConfig: DisplayConfig<JobStats> = {
  submitted_count: "Submitted",
  progressing_count: "Progressing",
  completed_count: "Complete",
  registered_count: "Registered",
  success_count: "Success",
  failed_count: "Failed",
  unsuccessful_count: "Unsuccessful",
  error_count: "Error",
  inactive_count: "Inactive",
  blocked_count: "Blocked",
};

const taskStatsDisplayConfig: DisplayConfig<TaskStats> = {
  avg_wait_time: {
    label: "Average Wait Time (seconds)",
    formatter: (v: number) => Math.round(v / 1000).toString(),
  },
  avg_encode_time: {
    label: "Average Encode Time (seconds)",
    formatter: (v: number) => Math.round(v / 1000).toString(),
  },
};

const generateDisplayRows = <T,>(data: T, displayConfig: DisplayConfig<T>): DisplayItem[] => {
  if (!data) {
    return [];
  }

  return Object.keys(displayConfig)
    .map((key) => {
      const config = displayConfig[key as keyof T];
      const value = data[key as keyof T];

      if (typeof config === "string") {
        // Case when config is just the label
        return { label: config, value: value };
      }

      if (typeof config === "object") {
        if (config.hide) {
          return null; // Return null to hide the entry
        }

        // Case when config contains label and formatter
        const formattedValue = config.formatter(value);
        return { label: config.label, value: formattedValue };
      }

      return null; // Fallback in case of unexpected config type
    })
    .filter((item): item is DisplayItem => item !== null); // Filter out any null results
};

const Stats: React.FC = () => {
  const { config } = useConfig();
  const [mainQueuePromise] = useState(promiseWithResolvers<QueueInfo>());
  const [backfillQueuePromise] = useState(promiseWithResolvers<QueueInfo>());
  const [retryQueuePromise] = useState(promiseWithResolvers<QueueInfo>());
  const [backendConfigurationPromise, setBackendConfigurationPromise] =
    useState(promiseWithResolvers<DisplayItem[]>());
  const [jobStatsPromise] = useState(promiseWithResolvers<DisplayItem[]>());
  const [taskStatsPromise] = useState(promiseWithResolvers<DisplayItem[]>());

  useEffect(() => {
    Objects.Stats.getQueueStats()
      .then((queueStats) => {
        mainQueuePromise.resolve({
          messageCount: queueStats.main.messages,
          queueLink: queueStats.main.queue_url,
        });

        backfillQueuePromise.resolve({
          messageCount: queueStats.backfill.messages,
          queueLink: queueStats.backfill.queue_url,
        });

        retryQueuePromise.resolve({
          messageCount: queueStats.retry.messages,
          queueLink: queueStats.retry.queue_url,
        });
      })
      .catch((reason) => {
        mainQueuePromise.reject(reason);
        backfillQueuePromise.reject(reason);
        retryQueuePromise.reject(reason);
      });
  }, [mainQueuePromise, backfillQueuePromise, retryQueuePromise]);

  useEffect(() => {
    // Wait unit config is loaded to update the display config
    if (config.primaryAccountName) {
      if (typeof backendConfigurationDisplayConfig.primary_stitcher_regions === "object") {
        backendConfigurationDisplayConfig.primary_stitcher_regions.label = `${config.primaryAccountName} Stitcher Regions`;
      }
      backendConfigurationDisplayConfig.primary_media_file_host = `${config.primaryAccountName} Media File Host`;
      if (typeof backendConfigurationDisplayConfig.secondary_stitcher_regions === "object") {
        backendConfigurationDisplayConfig.secondary_stitcher_regions.label = `${config.secondaryAccountName} Stitcher Regions`;
        backendConfigurationDisplayConfig.secondary_stitcher_regions.hide = config.singleTenantMode;
      }
      if (typeof backendConfigurationDisplayConfig.secondary_media_file_host === "object") {
        backendConfigurationDisplayConfig.secondary_media_file_host.label = `${config.secondaryAccountName} Media File Host`;
        backendConfigurationDisplayConfig.secondary_media_file_host.hide = config.singleTenantMode;
      }

      const newBackendConfigurationPromise = promiseWithResolvers<DisplayItem[]>();
      setBackendConfigurationPromise(newBackendConfigurationPromise);

      Objects.Stats.getBackendConfiguration()
        .then((result) => {
          newBackendConfigurationPromise.resolve(
            generateDisplayRows(result, backendConfigurationDisplayConfig),
          );
        })
        .catch(newBackendConfigurationPromise.reject);
    }
  }, [config.primaryAccountName, config.secondaryAccountName, config.singleTenantMode]);

  useEffect(() => {
    Objects.Stats.getJobStats()
      .then((result) => jobStatsPromise.resolve(generateDisplayRows(result, jobStatsDisplayConfig)))
      .catch(jobStatsPromise.reject);
  }, [jobStatsPromise]);

  useEffect(() => {
    Objects.Stats.getTaskStats()
      .then((result) => taskStatsPromise.resolve(generateDisplayRows(result, taskStatsDisplayConfig)))
      .catch(taskStatsPromise.reject);
  }, [taskStatsPromise]);

  return (
    <PageContainer>
      <Box sx={{ flexGrow: 1, pt: "16px" }}>
        <Typography variant="h6">📊 QUEUE STATISTICS</Typography>
        <Grid container spacing={{ xs: 2, md: 3 }} columns={{ xs: 4, sm: 8, md: 12 }}>
          <Grid item xs={4} sm={8} md={4}>
            <QueueCard
              title="Main Queue"
              getQueueInfo={() => mainQueuePromise.promise}
              purgeAction={Objects.Stats.purgeMainQueue}
            ></QueueCard>
          </Grid>
          <Grid item xs={4} sm={8} md={4}>
            <QueueCard
              title="Backfill Queue"
              getQueueInfo={() => backfillQueuePromise.promise}
              purgeAction={Objects.Stats.purgeBackfillQueue}
            ></QueueCard>
          </Grid>
          <Grid item xs={4} sm={8} md={4}>
            <QueueCard
              title="Retry Queue"
              getQueueInfo={() => retryQueuePromise.promise}
              purgeAction={Objects.Stats.purgeRetryQueue}
            ></QueueCard>
          </Grid>

          <Grid item xs={8} sm={8} md={8}>
            <StatsColumn
              title="⚙️ CONFIGURATION"
              getStats={() => backendConfigurationPromise.promise}
            ></StatsColumn>
          </Grid>
          <Grid item xs={8} sm={8} md={4}>
            <StatsColumn title={"📈 JOB STATISTICS"} getStats={() => jobStatsPromise.promise}></StatsColumn>
            <Box sx={{ pt: "22px" }}>
              <StatsColumn
                title={"⏳ TASK TIME (seconds)"}
                getStats={() => taskStatsPromise.promise}
              ></StatsColumn>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </PageContainer>
  );
};

export default Stats;
